import type { JSX } from 'react'
import { MessageSquare } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface FeedbackButtonProps {
  onClick: () => void
  className?: string
}

/**
 * The always-visible way to report a problem.
 *
 * Both existing routes were effectively closed. Ctrl+Shift+B is undiscoverable
 * — nobody presses a shortcut they were never told about — and the menu item in
 * the user dropdown was unreachable in practice: the header carries
 * `backdrop-blur`, which creates a stacking context, while being
 * `position: static`, so ordinary positioned canvas content painted over the
 * whole dropdown. A user who did not already know the shortcut had no way in.
 *
 * Mounted app-wide by `SupportBundleHost` rather than on the canvas, for the
 * same reason the shortcut is: "the import wizard hung" and "the dashboard
 * won't load" are exactly the reports that cannot be reproduced today, and off
 * the editor the bundle simply carries no model.
 *
 * `z-40` is deliberate and sits between two things. Above the canvas overlays
 * (`z-10`, `z-20`) so it is not buried by the colour scale or the 3D controls.
 * Below the modal dialogs (`z-[60]`, `z-[100]`) so it does not float over a
 * pump curve or a valve picker the user is working in — including the
 * diagnostics dialog this very button opens.
 */
export function FeedbackButton(props: FeedbackButtonProps): JSX.Element {
  const { onClick, className } = props

  return (
    <button
      type="button"
      onClick={onClick}
      // The accessible name, matching the menu item and the dialog title
      // exactly. Two affordances for one action should not have two names.
      aria-label="Send feedback"
      title="Send feedback"
      className={cn(
        'fixed bottom-4 right-4 z-40 flex h-11 w-11 items-center justify-center',
        'rounded-full border bg-background/95 text-muted-foreground shadow-lg backdrop-blur',
        'transition-colors hover:bg-accent hover:text-accent-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      {/* A speech bubble, not a bug.
          The button carries no visible text, so the glyph is the only thing
          inviting anyone to press it — and on a touch device there is no hover
          title either. A bug icon asks for defects, which contradicts copy
          written to invite praise and suggestions as well, and would quietly
          bias what gets reported.

          Decorative: the accessible name is on the button. Without aria-hidden
          the SVG is announced and the control reads as unlabelled. */}
      <MessageSquare className="h-5 w-5" aria-hidden="true" />
    </button>
  )
}
